import { StylesConfig } from "react-select";

export type SelectFieldOption = {
  label: string;
  value: string;
  isDisabled?: boolean;
};

export const getSelectFieldStyles = (
  hasError = false,
): StylesConfig<SelectFieldOption, false> => ({
  control: (base, state) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.04)",
    borderColor: hasError
      ? "var(--brand-error-border)"
      : state.isFocused
        ? "var(--brand-highlight)"
        : "var(--brand-border)",
    borderRadius: "1rem",
    boxShadow: hasError
      ? "0 0 0 3px var(--brand-error-ring)"
      : state.isFocused
        ? "var(--brand-input-focus-ring)"
        : "none",
    minHeight: "3.35rem",
    paddingLeft: "0.1rem",
    transition: "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
    "&:hover": {
      borderColor: hasError ? "var(--brand-error-border)" : "var(--brand-highlight)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 0.75rem",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--brand-ink)",
    opacity: 0.88,
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--brand-ink)",
  }),
  input: (base) => ({
    ...base,
    color: "var(--brand-ink)",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    background: "#151b23",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "1rem",
    boxShadow: "0 18px 36px rgba(0, 0, 0, 0.38)",
    overflow: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    padding: "0.35rem",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? "rgba(240, 191, 86, 0.18)"
      : state.isFocused
        ? "rgba(120, 167, 222, 0.12)"
        : "#151b23",
    borderRadius: "0.7rem",
    color: state.isDisabled ? "var(--brand-muted)" : "var(--brand-ink)",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "var(--brand-highlight)" : "var(--brand-muted)",
    "&:hover": {
      color: "var(--brand-highlight)",
    },
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--brand-muted)",
    "&:hover": {
      color: "var(--brand-highlight)",
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: "var(--brand-muted)",
  }),
});

export const getCompactSelectFieldStyles = (): StylesConfig<SelectFieldOption, false> => {
  const base = getSelectFieldStyles(false);

  return {
    ...base,
    control: (baseStyles, state) => ({
      ...(base.control?.(baseStyles, state) ?? baseStyles),
      borderRadius: "0.95rem",
      cursor: "pointer",
      minHeight: "2.65rem",
      fontSize: "0.82rem",
      fontWeight: 600,
    }),
    valueContainer: (baseStyles, state) => ({
      ...(base.valueContainer?.(baseStyles, state) ?? baseStyles),
      padding: "0 0.65rem",
    }),
    dropdownIndicator: (baseStyles, state) => ({
      ...(base.dropdownIndicator?.(baseStyles, state) ?? baseStyles),
      padding: "0.35rem",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    option: (baseStyles, state) => ({
      ...(base.option?.(baseStyles, state) ?? baseStyles),
      fontSize: "0.82rem",
      fontWeight: 600,
      padding: "0.55rem 0.7rem",
    }),
  };
};
export type SchoolSelectOption = SelectFieldOption & {
  imageSrc?: string | null;
  menuLabel?: string;
};

export const getSchoolSelectorStyles = (): StylesConfig<SchoolSelectOption, false> => {
  const base = getSelectFieldStyles(false);

  return {
    ...base,
    control: (baseStyles, state) => ({
      ...(base.control?.(baseStyles, state) ?? baseStyles),
      background: "transparent",
      border: "none",
      borderRadius: 0,
      boxShadow: "none",
      cursor: "pointer",
      height: "2.25rem",
      minHeight: "2.25rem",
      "&:hover": {
        borderColor: "transparent",
      },
    }),
    valueContainer: (baseStyles, state) => ({
      ...(base.valueContainer?.(baseStyles, state) ?? baseStyles),
      flexWrap: "nowrap",
      overflow: "hidden",
      padding: "0",
    }),
    singleValue: (baseStyles, state) => ({
      ...(base.singleValue?.(baseStyles, state) ?? baseStyles),
      margin: 0,
      maxWidth: "100%",
      overflow: "hidden",
      position: "static",
      textOverflow: "ellipsis",
      transform: "none",
      whiteSpace: "nowrap",
    }),
    placeholder: (baseStyles, state) => ({
      ...(base.placeholder?.(baseStyles, state) ?? baseStyles),
      margin: 0,
      maxWidth: "100%",
      overflow: "hidden",
      position: "static",
      textOverflow: "ellipsis",
      transform: "none",
      whiteSpace: "nowrap",
    }),
    input: (baseStyles, state) => ({
      ...(base.input?.(baseStyles, state) ?? baseStyles),
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (baseStyles, state) => ({
      ...(base.indicatorsContainer?.(baseStyles, state) ?? baseStyles),
      height: "2.25rem",
      paddingRight: 0,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (baseStyles, state) => ({
      ...(base.dropdownIndicator?.(baseStyles, state) ?? baseStyles),
      padding: "0 0 0 0.25rem",
    }),
    option: (baseStyles, state) => ({
      ...(base.option?.(baseStyles, state) ?? baseStyles),
      alignItems: "center",
      display: "flex",
      gap: "0.65rem",
    }),
  };
};
